import { useRef, useEffect } from 'react';
import {
  useTranslation,
  NextgenImage,
  ButtonStyles,
  FAIcon,
  Theme,
  ButtonWidth,
  LoaderType,
} from '@nextgen-web-framework/all';
import { ProfileImagesDataType } from '@commerce-profile-webcomponents/sdk';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import { useMyAccount } from '../../../utils/myAccountHook';
import { useFeatureFlag } from '../../../utils/featureFlagHook';
import { CoAppCommonScreenProps, CoAppDisplayItem, CoAppScreenEnum } from '../types/propTypes';
import ButtonWithLoading from '../../Common/ButtonWithLoading/ButtonWithLoading';
import styles from './CoAppCommonScreen.module.scss';

const CoAppCommonScreen = ({
  buttonHandler,
  screenType,
  buttonLoading,
  disableButton,
  buttonMessage,
  buttonMessageTheme,
}: CoAppCommonScreenProps) => {
  const t = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useMyAccount();

  const { enableCoAppAdd: enableAddCoApp } = useFeatureFlag();

  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const { coApp: coAppCMSData } = registrationCmsData as ProfileImagesDataType;

  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { userData } = registrationFlowData;
  const { accountNum: newAccountNum = '' } = userData;

  const addNewButtonTxt = enableAddCoApp ? t('profile.coApp.button.addCoApplicant') : '';

  // Display setting based on step
  const configData: Record<string, CoAppDisplayItem> = {
    OTP_VERIFY: {
      image: coAppCMSData?.landingImage,
      height: 180,
      subHeaderTxt2: t('profile.coApp.common.youCanAddBusinessPartnetAsCoApp'),
      buttonTxt: addNewButtonTxt,
    },

    INVITATION_SENT: {
      iconChecked: true,
      height: 180,
      headerTxt: t('profile.coApp.common.lintSentToCoApplicant'),
      subHeaderTxt2: t('profile.coApp.common.remindCoApplicantToFillUp'),
      buttonTxt: t('profile.coApp.button.back'),
    },

    PENDING: {
      image: coAppCMSData?.pendingImage,
      height: 180,
      subHeaderTxt2: t('profile.coApp.common.requestSent'),
      buttonTxt: t('profile.coApp.button.resend'),
    },

    COMPLETED: {
      image: coAppCMSData?.completedImage,
      height: 180,
      headerTxt: t('profile.coApp.common.welcomeText'),
      subHeaderTxt1: t('profile.coApp.common.yourAccountNumber', { accountNumber: newAccountNum }),
      subHeaderTxt2: t('profile.coApp.common.signInAccountMethod'),
      buttonTxt: t('profile.coApp.button.home'),
    },
  };

  const handleButton = () => {
    buttonHandler?.();
  };

  useEffect(() => {
    // Initial check screen and scroll
    handleResize();

    // To detect screen scroll & resize
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      // To reset the event listener
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  // UI functions
  const handleResize = () => {
    if (!isMobile && contentRef.current) {
      const contentTop = contentRef.current.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;
      if (contentTop < windowHeight && contentTop > 0) {
        contentRef.current.style.height = `${windowHeight - contentTop}px`;
      }
    }
  };

  const contentData = configData[screenType];

  return (
    <section
      className={`${styles['co-app-common-container']} ${styles[screenType]}`}
      data-testid="co-app-common-container"
    >
      {!isMobile && screenType !== CoAppScreenEnum.COMPLETED && (
        <section className={styles['header']} data-testid="co-app-header">
          <div className={styles['header-title']}>
            <div className={styles['text']} data-testid="co-app-header-title">
              {t('profile.coApp.screenTitle')}
            </div>
          </div>
        </section>
      )}
      <div className={styles['co-app-common-content']} data-testid="co-app-common-content" ref={contentRef}>
        <div className={styles['co-app-common']} data-testid="co-app-common">
          {contentData?.image && (
            <div className={styles['co-app-img']}>
              <NextgenImage
                src={`${contentData.image.url}`}
                loaderType={LoaderType.CONTENTSTACK}
                alt={`${contentData.image.altText}`}
                data-testid="co-app-image-testid"
              />
            </div>
          )}
          {contentData?.iconChecked && (
            <FAIcon
              iconName="check-circle"
              theme={Theme.SUCCESS}
              className={styles['co-app-icon']}
              data-testid="checked-icon-test-id"
            />
          )}
          {contentData?.headerTxt && <div className={styles['co-app-header']}>{contentData?.headerTxt}</div>}
          {contentData?.subHeaderTxt1 && (
            <div className={styles['co-app-sub-header1']}>{contentData.subHeaderTxt1}</div>
          )}
          {contentData?.subHeaderTxt2 && (
            <div className={styles['co-app-sub-header2']}>{contentData.subHeaderTxt2}</div>
          )}
          {contentData?.buttonTxt && (
            <div className={styles['co-app-button']}>
              <ButtonWithLoading
                title={contentData?.buttonTxt}
                buttonStyle={ButtonStyles.SOLID}
                onClick={handleButton}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                data-testid="co-app-button-testid"
                isLoading={buttonLoading}
                disabled={disableButton}
              />
            </div>
          )}
          {buttonMessage && (
            <div className={styles[`button-message-${buttonMessageTheme}`]} data-testid="co-app-button-message-testid">
              {buttonMessage}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CoAppCommonScreen;
